import React, { useState } from 'react';
import { FileText, ClipboardCheck, Mic, MapPin, Plane, CheckCircle, ArrowUpRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { services } from '../mock';
import Reveal from './shared/Reveal';
import SectionHeader from './shared/SectionHeader';

const iconMap = {
  'file-text': FileText,
  'clipboard-check': ClipboardCheck,
  mic: Mic,
  'map-pin': MapPin,
  plane: Plane
};

const teasers = {
  1: 'Narratives that win over admission committees.',
  2: 'End-to-end visa support, zero guesswork.',
  3: 'Rehearse with experts before the real thing.',
  4: 'Settled in from the moment you land.',
  5: 'Best fares, flexible dates, covered travel.'
};

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);

  return (
    <>
      <section id="services" className="py-20 lg:py-28 bg-white relative overflow-hidden">
        {/* Subtle background accents */}
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand-50 blur-3xl opacity-70 pointer-events-none"></div>
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-navy-50 blur-3xl opacity-70 pointer-events-none"></div>

        <div className="container-x relative">
          <SectionHeader
            eyebrow="What we do"
            title={<>Everything you need, <span className="text-brand-500">under one roof</span></>}
            subtitle="From the first draft of your SOP to your first week abroad — five services that cover the entire journey."
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <Reveal key={service.id} delay={index * 90}>
                  <button
                    onClick={() => setSelectedService(service)}
                    className="card-premium group w-full text-left p-7 lg:p-8 relative h-full"
                  >
                    {/* Index number */}
                    <span className="absolute top-6 right-7 font-display text-4xl font-extrabold text-navy-50 group-hover:text-brand-100 transition-colors select-none">
                      0{index + 1}
                    </span>

                    <span className="w-12 h-12 rounded-xl bg-navy-900 text-brand-400 flex items-center justify-center shadow-card group-hover:bg-brand-500 group-hover:text-white group-hover:scale-110 transition-all duration-300">
                      {Icon && <Icon className="w-6 h-6" />}
                    </span>

                    <h3 className="mt-5 font-display text-lg lg:text-xl font-bold text-navy-900 group-hover:text-brand-600 transition-colors">
                      {service.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                      {teasers[service.id]}
                    </p>

                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-navy-900 group-hover:text-brand-600 transition-colors">
                      Learn more
                      <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </button>
                </Reveal>
              );
            })}

            {/* Filler tile turning the 5-card grid into a clean 6-slot layout */}
            <Reveal delay={450}>
              <div className="h-full rounded-2xl bg-navy-900 p-7 lg:p-8 flex flex-col justify-between text-white relative overflow-hidden">
                <div className="absolute -right-10 -bottom-10 w-44 h-44 rounded-full bg-brand-500/20 blur-2xl"></div>
                <div>
                  <h3 className="font-display text-lg lg:text-xl font-bold">Not sure where to start?</h3>
                  <p className="mt-2 text-sm text-navy-100/80 leading-relaxed">
                    Talk to a counselor — get a free profile evaluation and a roadmap built for you.
                  </p>
                </div>
                <a
                  href="https://wa.me/919059333987?text=Hi%2C%20I%20would%20like%20a%20free%20profile%20evaluation."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brand-500 hover:bg-brand-600 px-6 py-3 text-sm font-semibold transition-colors w-fit"
                >
                  Free Consultation
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Service Detail Modal */}
      <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
        <DialogContent className="sm:max-w-2xl rounded-2xl">
          <DialogHeader>
            <div className="flex items-center gap-4">
              <span className="w-14 h-14 rounded-2xl bg-navy-900 text-brand-400 flex items-center justify-center shrink-0">
                {selectedService &&
                  React.createElement(iconMap[selectedService.icon], { className: 'w-7 h-7' })}
              </span>
              <div>
                <DialogTitle className="font-display text-2xl font-bold text-navy-900 text-left">
                  {selectedService?.title}
                </DialogTitle>
                <p className="text-sm text-slate-500 text-left mt-0.5">Xpert Advisor service</p>
              </div>
            </div>
          </DialogHeader>

          {selectedService && (
            <div className="space-y-6">
              <p className="text-slate-600 leading-relaxed">{selectedService.description}</p>

              <div>
                <h4 className="text-sm font-bold tracking-wide uppercase text-slate-400 mb-3">What's included</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {selectedService.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-2.5 p-3 bg-navy-50 rounded-xl">
                      <CheckCircle className="w-5 h-5 text-brand-500 shrink-0" />
                      <span className="text-sm font-medium text-navy-800">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button onClick={() => setSelectedService(null)} className="btn-primary w-full">
                Get Started with This Service
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Services;
